/**
 * Scaling Up Meetings — list + start (Daily/Weekly/Monthly/Quarterly/Annual),
 * live meeting runner: agenda, notes, inline action items, end + AI summary.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Play, Square, Sparkles, Plus } from "lucide-react";

type MeetingType = "daily" | "weekly" | "monthly" | "quarterly" | "annual";

interface Holding {
  id: string;
  name: string;
}
interface Company {
  id: string;
  name: string;
}
interface Meeting {
  id: string;
  type: MeetingType;
  scheduledAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  attendees: string[];
  notes: string | null;
  aiSummary: string | null;
}

interface ActionItem {
  id: string;
  description: string;
  ownerUserId: string | null;
  dueDate: string | null;
  status: string;
}

function ScalingUpMeetingsInner() {
  const qc = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [activeMeetingId, setActiveMeetingId] = useState<string | null>(null);

  const holdingsQuery = useQuery<{ items: Holding[] }>({ queryKey: ["/api/scaling-up/holdings"] });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });
  const meetingsQueryKey: readonly string[] = [
    "/api/scaling-up/companies",
    companyId ?? "",
    "meetings",
  ];
  const meetingsQuery = useQuery<{ items: Meeting[] }>({
    queryKey: meetingsQueryKey,
    enabled: companyId !== null,
  });

  const startMutation = useMutation({
    mutationFn: async (type: MeetingType) =>
      apiRequest<Meeting>("POST", `/api/scaling-up/companies/${companyId}/meetings/start`, {
        type,
      }),
    onSuccess: (created: Meeting) => {
      setActiveMeetingId(created.id);
      qc.invalidateQueries({ queryKey: meetingsQueryKey });
    },
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const meetings = meetingsQuery.data?.items ?? [];

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <header className="mb-6">
        <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Scaling Up</div>
        <h1 className="text-2xl lg:text-3xl font-extrabold">Meeting Rhythm</h1>
      </header>

      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId(null);
            }}
          >
            <option value="">Holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(e.target.value || null)}
            disabled={!holdingId}
          >
            <option value="">Spółka…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {companyId && (
        <Card className="mb-4 border-white/10 bg-white/[0.04]">
          <CardContent className="p-4 flex items-center gap-2 flex-wrap">
            <span className="text-white/60 text-sm mr-2">Rozpocznij:</span>
            {(["daily", "weekly", "monthly", "quarterly", "annual"] as MeetingType[]).map((t) => (
              <Button
                key={t}
                size="sm"
                variant="outline"
                onClick={() => startMutation.mutate(t)}
                disabled={startMutation.isPending}
              >
                <Play className="w-3 h-3 mr-1" /> {t}
              </Button>
            ))}
            {startMutation.isPending && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
          </CardContent>
        </Card>
      )}

      {activeMeetingId && (
        <MeetingRunner
          meetingId={activeMeetingId}
          companyId={companyId}
          onClose={() => {
            setActiveMeetingId(null);
            qc.invalidateQueries({ queryKey: meetingsQueryKey });
          }}
        />
      )}

      {meetingsQuery.isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border-white/10 bg-white/[0.02]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
                <Skeleton className="h-3 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {meetings.map((m) => (
          <Card key={m.id} className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold capitalize">{m.type}</div>
                <Badge
                  className={`border ${m.endedAt ? "bg-white/5 border-white/10 text-white/60" : "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"}`}
                >
                  {m.endedAt ? "closed" : "live"}
                </Badge>
              </div>
              <div className="text-xs text-white/50">
                {m.startedAt && `Start: ${new Date(m.startedAt).toLocaleString()}`}
                {m.endedAt && ` · Koniec: ${new Date(m.endedAt).toLocaleString()}`}
              </div>
              {!m.endedAt && (
                <Button size="sm" className="mt-3" onClick={() => setActiveMeetingId(m.id)}>
                  Otwórz runner
                </Button>
              )}
              {m.aiSummary && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-white/60">AI summary</summary>
                  <pre className="mt-2 whitespace-pre-wrap text-white/70 max-h-60 overflow-auto">
                    {m.aiSummary}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MeetingRunner({
  meetingId,
  companyId,
  onClose,
}: {
  meetingId: string;
  companyId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [actionDesc, setActionDesc] = useState("");
  const [actionDue, setActionDue] = useState("");

  const actionsKey: readonly string[] = ["/api/scaling-up/meetings", meetingId, "action-items"];
  const actionsQuery = useQuery<{ items: ActionItem[] }>({ queryKey: actionsKey });

  const saveNotesMutation = useMutation({
    mutationFn: async () =>
      apiRequest("PUT", `/api/scaling-up/meetings/${meetingId}/notes`, { notes }),
  });

  const addActionMutation = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("companyId required");
      return apiRequest("POST", `/api/scaling-up/companies/${companyId}/action-items`, {
        description: actionDesc,
        dueDate: actionDue || null,
        meetingId,
      });
    },
    onSuccess: () => {
      setActionDesc("");
      setActionDue("");
      qc.invalidateQueries({ queryKey: actionsKey });
    },
  });

  const summarizeMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/scaling-up/ai/meeting-summarizer", { meetingId }),
  });

  const endMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/scaling-up/meetings/${meetingId}/end`),
    onSuccess: onClose,
  });

  const items = actionsQuery.data?.items ?? [];

  return (
    <Card className="mb-4 border-emerald-500/30 bg-emerald-500/[0.04]">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-emerald-300 text-xs uppercase tracking-wider">Live meeting</div>
            <div className="font-semibold">Meeting {meetingId.slice(0, 8)}…</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => summarizeMutation.mutate()}
              disabled={summarizeMutation.isPending}
            >
              {summarizeMutation.isPending ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3 mr-1" />
              )}
              AI Summary
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => endMutation.mutate()}
              disabled={endMutation.isPending}
            >
              <Square className="w-3 h-3 mr-1" /> Zakończ
            </Button>
          </div>
        </div>

        <div>
          <div className="text-xs text-white/50 mb-1">Notes</div>
          <textarea
            className="w-full bg-black/30 border border-white/10 rounded p-3 text-sm h-32"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => saveNotesMutation.mutate()}
            placeholder="Surowe notatki — AI Summary wyciągnie z nich action items + issues."
          />
          {saveNotesMutation.isSuccess && (
            <div className="text-[10px] text-emerald-300 mt-1">Zapisano</div>
          )}
        </div>

        <div>
          <div className="text-xs text-white/50 mb-1">Action items (Who-What-When)</div>
          {actionsQuery.isLoading ? (
            <ul className="space-y-1 mb-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <li
                  key={i}
                  className="flex justify-between gap-2 border border-white/10 rounded px-3 py-1.5"
                >
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-12" />
                </li>
              ))}
            </ul>
          ) : (
            <ul className="space-y-1 mb-2">
              {items.map((a) => (
                <li
                  key={a.id}
                  className="text-sm flex justify-between gap-2 border border-white/10 rounded px-3 py-1.5"
                >
                  <span>{a.description}</span>
                  <span className="text-white/40 text-xs">{a.dueDate ?? "—"}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <input
              className="flex-1 bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm"
              placeholder="Co? (np. 'Maciej przygotuje analizę X')"
              value={actionDesc}
              onChange={(e) => setActionDesc(e.target.value)}
            />
            <input
              type="date"
              className="bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm"
              value={actionDue}
              onChange={(e) => setActionDue(e.target.value)}
            />
            <Button
              size="sm"
              onClick={() => addActionMutation.mutate()}
              disabled={!actionDesc || addActionMutation.isPending}
            >
              <Plus className="w-3 h-3 mr-1" /> Dodaj
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ScalingUpMeetingsPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpMeetingsInner />
    </AdminGuard>
  );
}
